import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import TrafficConfigTable from './TrafficConfigTable';
import Badge from '../../components/atoms/Badge';
import Button from '../../components/atoms/forms/Button';
import VehicleTypeCard from './VehicleTypeCard';
import CollapsibleSection from '../../components/CollapsibleSection';
import DropdownItem from '../../components/atoms/DropdownItem';
import theme from '../../theme';
import dropdownStyles from './styles';

const StyledDiv = styledComponent('div', {
  '& .fc-card': {
    background: theme.colors.bgCard,
    borderRadius: theme.radii.card,
    border: theme.borders.card,
    boxShadow: theme.shadows.card,
    overflow: 'hidden',
    marginBottom: theme.spacing.sectionGap,
  },

  '& .fc-header': {
    background: theme.colors.primary,
    color: theme.colors.white,
    padding: theme.spacing.headerPadding,
    ...theme.typography.sectionHeader,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  '& .fc-header-title': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapLg,
  },

  '& .add-btn': {
    background: theme.colors.white,
    color: theme.colors.primary,
    border: 'none',
    padding: theme.spacing.addButtonPadding,
    borderRadius: theme.radii.input,
    ...theme.typography.smallButton,
    cursor: 'pointer',
    transition: theme.transitions.default,
    '&:hover': {
      opacity: 0.9,
    },
  },

  '& .fc-body': {
    padding: theme.spacing.cardPadding,
  },

  '& .fc-empty-state': {
    textAlign: 'center',
    padding: theme.spacing.emptyStatePadding,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
  },

  '& .traffic-buttons': {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing.gapLg,
    marginTop: theme.spacing.gapLg,
  },

  // Shared styles
  ...dropdownStyles,
});

function FCSectionCard({
  fc,
  timezone,
  vehicles,
  onAddVehicle,
  onRemoveVehicle,
  onVehicleChange,
  isSubmitting,
  trafficConfig,
  originalTrafficConfig,
  trafficConfigExpanded,
  trafficConfigLoading,
  onToggleTrafficExpanded,
  onTrafficConfigChange,
  onSaveTrafficConfig,
  onResetTrafficConfig,
  hasTrafficModifications,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  function handleAddVehicle(shiftType, e) {
    e.stopPropagation();
    onAddVehicle(shiftType);
    setDropdownOpen(false);
  }

  return (
    <StyledDiv>
      <div className="fc-card">
        {/* Header */}
        <div className="fc-header">
          <div className="fc-header-title">
            <span>{fc} Vehicles</span>
            {timezone && <Badge variant="muted">{timezone}</Badge>}
          </div>

          {/* Add Vehicle Type dropdown */}
          <div className="dropdown-container">
            <button
              type="button"
              className="add-btn"
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              + Add Vehicle Type
            </button>

            {dropdownOpen && (
              <div
                className="dropdown-overlay"
                onClick={() => setDropdownOpen(false)}
              />
            )}

            <div className={`dropdown-content${dropdownOpen ? ' show' : ''}`}>
              <DropdownItem
                label="AM Shift"
                hint="Starts early morning"
                onClick={(e) => handleAddVehicle('AM', e)}
              />
              <DropdownItem
                label="PM Shift"
                hint="Starts afternoon/evening"
                onClick={(e) => handleAddVehicle('PM', e)}
              />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="fc-body">
          {vehicles && vehicles.length > 0 ? (
            vehicles.map((vehicle, index) => (
              <VehicleTypeCard
                key={vehicle.id}
                vehicle={vehicle}
                index={index}
                timezone={timezone}
                canRemove={vehicles.length > 1}
                onChange={(field, value) =>
                  onVehicleChange(vehicle.id, field, value)
                }
                onRemove={() => onRemoveVehicle(vehicle.id)}
              />
            ))
          ) : (
            <div className="fc-empty-state">
              No vehicle types added yet. Use &ldquo;Add Vehicle Type&rdquo; to
              get started.
            </div>
          )}

          {/* Traffic Configuration */}
          <CollapsibleSection
            title="Traffic Configuration"
            badge={hasTrafficModifications ? 'Modified' : null}
            expanded={trafficConfigExpanded}
            onToggle={onToggleTrafficExpanded}
            loading={trafficConfigLoading}
            loadingText="Loading traffic configuration..."
          >
            <TrafficConfigTable
              weekdayData={trafficConfig?.weekday}
              weekendData={trafficConfig?.weekend}
              originalWeekdayData={originalTrafficConfig?.weekday}
              originalWeekendData={originalTrafficConfig?.weekend}
              onWeekdayChange={(updated) =>
                onTrafficConfigChange('weekday', updated)
              }
              onWeekendChange={(updated) =>
                onTrafficConfigChange('weekend', updated)
              }
              disabled={isSubmitting}
            />
            <div className="traffic-buttons">
              <Button
                variant="primary"
                onClick={onSaveTrafficConfig}
                disabled={isSubmitting || !hasTrafficModifications}
              >
                Save
              </Button>
              <Button
                variant="warning-outline"
                onClick={onResetTrafficConfig}
                disabled={isSubmitting || !hasTrafficModifications}
              >
                Reset
              </Button>
            </div>
          </CollapsibleSection>
        </div>
      </div>
    </StyledDiv>
  );
}

FCSectionCard.propTypes = {
  fc: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  vehicles: PropTypes.arrayOf(PropTypes.object),
  onAddVehicle: PropTypes.func.isRequired,
  onRemoveVehicle: PropTypes.func.isRequired,
  onVehicleChange: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  trafficConfig: PropTypes.shape({
    weekday: PropTypes.array,
    weekend: PropTypes.array,
  }),
  originalTrafficConfig: PropTypes.shape({
    weekday: PropTypes.array,
    weekend: PropTypes.array,
  }),
  trafficConfigExpanded: PropTypes.bool,
  trafficConfigLoading: PropTypes.bool,
  onToggleTrafficExpanded: PropTypes.func.isRequired,
  onTrafficConfigChange: PropTypes.func.isRequired,
  onSaveTrafficConfig: PropTypes.func.isRequired,
  onResetTrafficConfig: PropTypes.func.isRequired,
  hasTrafficModifications: PropTypes.bool,
};

FCSectionCard.defaultProps = {
  timezone: null,
  vehicles: [],
  isSubmitting: false,
  trafficConfig: null,
  originalTrafficConfig: null,
  trafficConfigExpanded: false,
  trafficConfigLoading: false,
  hasTrafficModifications: false,
};

export default FCSectionCard;
